import { useMemo, useState } from "react";
import { copyText } from "./basket";
import { formatCopyItem, type CopyItem } from "./copy-format";
import { evidenceLabel } from "./evidence";
import type { Evidence, EvidenceSentence, SearchResult } from "./types";

function snippet(text: string, max: number): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  return normalized.length > max ? `${normalized.slice(0, max)}…` : normalized;
}

type EvidenceGroupKey = "statutes" | "citedCases" | "directCites" | "ruleHits" | "matchedTerms";

const GROUPS: Array<{ key: EvidenceGroupKey; label: string }> = [
  { key: "statutes", label: "조문" },
  { key: "citedCases", label: "인용 판례" },
  { key: "directCites", label: "직접 인용" },
  { key: "ruleHits", label: "규칙" },
  { key: "matchedTerms", label: "일치 용어" },
];

function normalized(evidence: Evidence | undefined): Evidence {
  return {
    statutes: evidence?.statutes ?? [],
    citedCases: evidence?.citedCases ?? [],
    directCites: evidence?.directCites ?? [],
    ruleHits: evidence?.ruleHits ?? [],
    matchedTerms: evidence?.matchedTerms ?? [],
    panel: evidence?.panel,
    provenance: evidence?.provenance,
  };
}

const PANEL_LABEL: Record<NonNullable<Evidence["panel"]>, string> = {
  direct: "직접 인용",
  related: "관련 판례",
  case: "판례",
};

const PROVENANCE_LABEL: Record<NonNullable<Evidence["provenance"]>, string> = {
  ref: "인용",
  body: "본문 인용",
};

function EvidenceChips({ evidence }: { evidence: Evidence }) {
  const groups = GROUPS.filter((group) => (evidence[group.key] ?? []).length > 0);
  const panel = evidence.panel;
  const provenance = evidence.provenance;
  if (groups.length === 0 && !panel && !provenance) {
    return <span className="result-tag result-tag-empty">근거 없음</span>;
  }
  return (
    <span className="result-tags">
      {panel ? (
        <span className={`result-tag result-tag-panel-${panel}`}>{PANEL_LABEL[panel]}</span>
      ) : null}
      {provenance ? (
        <span className={`result-tag result-tag-provenance-${provenance}`}>
          {PROVENANCE_LABEL[provenance]}
        </span>
      ) : null}
      {groups.map((group) => (
        <span key={group.key} className={`result-tag result-tag-${group.key}`}>
          {group.label} {(evidence[group.key] ?? []).length}
        </span>
      ))}
    </span>
  );
}

export function EvidenceList({ evidence }: { evidence: Evidence }) {
  const groups = GROUPS.filter((group) => (evidence[group.key] ?? []).length > 0);
  if (groups.length === 0) return null;
  return (
    <div className="result-evidence">
      {groups.map((group) => (
        <div key={group.key} className="result-evidence-row">
          <span className="result-evidence-label">{group.label}</span>
          <ul className="result-evidence-list">
            {(evidence[group.key] ?? []).map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function citationOf(result: SearchResult): string {
  return result.citation ?? result.label ?? result.pointer;
}

function caseCopyItem(result: SearchResult, sentence?: EvidenceSentence): CopyItem {
  return {
    kind: "case",
    citation: citationOf(result),
    ...(sentence
      ? { quote: sentence.text, quoteLabel: evidenceLabel(sentence) }
      : {}),
  };
}

/** Honesty chip: never label a related sentence as a body citation. */
export function EvidenceKindChip({ kind }: { kind: EvidenceSentence["kind"] }) {
  return (
    <span className={`result-evidence-kind is-${kind}`}>
      {kind === "citation" ? "본문 인용" : "관련 문장"}
    </span>
  );
}

/** 문장 하나/카드 하나를 원문 그대로 복사한다. 머리말·안내문은 넣지 않는다. */
function CopyButton({
  text,
  label,
  compact,
}: {
  text: string;
  label: string;
  compact?: boolean;
}) {
  const [state, setState] = useState<"idle" | "done" | "fail">("idle");
  return (
    <button
      type="button"
      className={`result-copy${compact ? " result-copy-compact" : ""}`}
      onClick={() => {
        void copyText(text).then((ok) => {
          setState(ok ? "done" : "fail");
          setTimeout(() => setState("idle"), ok ? 1200 : 1600);
        });
      }}
    >
      {state === "done" ? "복사됨" : state === "fail" ? "복사 실패" : label}
    </button>
  );
}

/** 판례 카드 본문: 근거 문장 + 문장별/카드별 복사. */
function CaseCardBody({ result }: { result: SearchResult }) {
  const sentences = result.evidenceSentences ?? [];
  return (
    <div className="result-body result-body-case">
      <div className="result-body-head">
        <span className="result-body-kicker">근거 문장</span>
        <CopyButton
          text={formatCopyItem(caseCopyItem(result, sentences[0]))}
          label="판례 복사"
        />
      </div>
      {sentences.length > 0 ? (
        <ol className="result-sentences">
          {sentences.map((sentence) => (
            <li key={sentence.index} className="result-sentence">
              <p className="result-sentence-text">{sentence.text}</p>
              <div className="result-sentence-foot">
                <span className="result-sentence-meta">
                  <EvidenceKindChip kind={sentence.kind} />
                  <span className="result-sentence-label">
                    {evidenceLabel(sentence)}
                  </span>
                </span>
                <CopyButton
                  text={formatCopyItem(caseCopyItem(result, sentence))}
                  label="문장 복사"
                  compact
                />
              </div>
            </li>
          ))}
        </ol>
      ) : result.text ? (
        <p className="result-sentence-text">{result.text}</p>
      ) : null}
    </div>
  );
}

export function ResultsView({
  results,
  onOpenCase,
  onSelect,
  selectedPointer,
  basketIds,
  onToggleBasket,
}: {
  results: SearchResult[];
  /** 판례 행을 누르면 전문 리더를 연다. 없으면 기존처럼 펼친다. */
  onOpenCase?: (result: SearchResult) => void;
  /** 넓은 워크스페이스: 행을 누르면 오른쪽 상세 패널에 표시한다. */
  onSelect?: (result: SearchResult) => void;
  selectedPointer?: string;
  /** 인용 바구니에 담긴 pointer 집합. 주어지면 체크박스를 그린다. */
  basketIds?: ReadonlySet<string>;
  onToggleBasket?: (result: SearchResult) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const items = useMemo(
    () => results.map((result) => ({ ...result, evidence: normalized(result.evidence) })),
    [results],
  );
  const sections = useMemo(() => {
    const out: Array<{
      panel: NonNullable<Evidence["panel"]>;
      items: typeof items;
    }> = [];
    for (const item of items) {
      const panel = item.panel ?? "direct";
      const last = out.at(-1);
      if (last?.panel === panel) last.items.push(item);
      else out.push({ panel, items: [item] });
    }
    return out;
  }, [items]);

  return (
    <div className="results">
      <div className="results-head">
        <span>검색 결과</span>
        <span className="results-count">{items.length}건</span>
      </div>
      {sections.map((section, sectionIndex) => (
        <section key={`${section.panel}-${sectionIndex}`} className="results-section">
          <div className="results-section-head">
            <span className={`result-tag result-tag-panel-${section.panel}`}>
              {PANEL_LABEL[section.panel]}
            </span>
            <span className="results-section-count">{section.items.length}건</span>
          </div>
          {section.items.map((result) => {
            const open = expanded === result.pointer;
            const isCase = Boolean(result.caseNo);
            const opensReader = isCase && onOpenCase !== undefined;
            const selected = selectedPointer === result.pointer;
            const checkable = basketIds !== undefined && onToggleBasket !== undefined;
            const title = isCase
              ? citationOf(result)
              : (result.label ?? result.pointer);
            return (
              <article
                key={`${result.pointer}-${result.rank}`}
                className={`result${open ? " is-open" : ""}${selected ? " is-selected" : ""}`}
              >
                <div className={`result-row${checkable ? " has-check" : ""}`}>
                  {checkable ? (
                    <label className="result-check">
                      <input
                        type="checkbox"
                        checked={basketIds?.has(result.pointer) ?? false}
                        onChange={() => onToggleBasket?.(result)}
                        aria-label={`${title} 인용 담기`}
                      />
                    </label>
                  ) : null}
                  <button
                    type="button"
                    className="result-head-button"
                    onClick={() => {
                      if (onSelect) onSelect(result);
                      else if (opensReader) onOpenCase?.(result);
                      else setExpanded(open ? null : result.pointer);
                    }}
                    aria-expanded={onSelect ? undefined : opensReader ? undefined : open}
                    aria-current={selected ? "true" : undefined}
                    aria-haspopup={!onSelect && opensReader ? "dialog" : undefined}
                  >
                    <span className="result-rank">{result.rank}</span>
                    <span className="result-main">
                      <span className="result-title">{title}</span>
                      {!isCase ? (
                        <span className="result-snippet">{snippet(result.text, 120)}</span>
                      ) : null}
                      <EvidenceChips evidence={result.evidence} />
                      {result.evidence.matchedTerms &&
                      result.evidence.matchedTerms.length > 0 ? (
                        <span className="result-match">
                          일치: {result.evidence.matchedTerms.slice(0, 8).join(" · ")}
                        </span>
                      ) : null}
                      {isCase && (opensReader || onSelect !== undefined) ? (
                        <span className="result-text-hint">
                          {result.caseTextStatus === "available" ? "전문 보기" : "판례 정보"}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </div>
                {isCase ? (
                  <CaseCardBody result={result} />
                ) : open ? (
                  <div className="result-body">
                    <p className="original">{result.text}</p>
                    <EvidenceList evidence={result.evidence} />
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>
      ))}
    </div>
  );
}
